import {
  BedDouble,
  ChevronDown,
  ChevronRight,
  MapPin,
  PersonStanding,
  Star,
} from 'lucide-react';
import { useState } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import { Badge, Heading, Text } from '@/shared/ui';
import css from './hyperguest-search.module.scss';

const cardStyles = pickCss(css, css.card);

// The upstream contract is wide and lives at the third party. We probe the
// minimum fields we render and treat everything else as opaque.
type HotelLike = {
  hotelId?: number | string;
  hotelInfo?: {
    name?: string;
    starRating?: number;
    cityName?: string;
    countryCode?: string;
  };
  rooms?: RoomLike[];
};

type RoomLike = {
  roomId?: string | number;
  roomName?: string;
  numberOfAvailableRooms?: number;
  pax?: { adults?: number; children?: unknown[] };
  beddingConfigurations?: Array<{ quantity?: number; type?: string }>;
  ratePlans?: RatePlanLike[];
};

type RatePlanLike = {
  ratePlanId?: string | number;
  ratePlanName?: string;
  payment?: string;
  inclusivePrice?: { amount?: number; currency?: string };
};

type Props = {
  result: unknown;
};

export const HotelResultCard = ({ result }: Props) => {
  const h = result as HotelLike;
  const [open, setOpen] = useState(true);
  const name = h.hotelInfo?.name ?? `Hotel #${h.hotelId ?? '—'}`;
  const location = [h.hotelInfo?.cityName, h.hotelInfo?.countryCode]
    .filter(Boolean)
    .join(', ');
  const rooms = (h.rooms ?? []).slice().sort((a, b) => {
    const av = a.numberOfAvailableRooms ?? 0;
    const bv = b.numberOfAvailableRooms ?? 0;
    return bv - av;
  });

  return (
    <div className={cardStyles({ isOpen: open })}>
      <button
        className={css.cardHeader}
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown /> : <ChevronRight />}
        <div className={css.cardHeaderTitle}>
          <Heading as="h3" size="sm">
            {name}
          </Heading>
          {location && (
            <div className={css.metaLine}>
              <MapPin className={css.metaIcon} />
              <Text size="xs" variant="muted">
                {location}
              </Text>
            </div>
          )}
        </div>
        {typeof h.hotelInfo?.starRating === 'number' && (
          <div className={css.stars}>
            {Array.from({ length: h.hotelInfo.starRating }).map((_, i) => (
              <Star key={i} className={css.starIcon} />
            ))}
          </div>
        )}
      </button>
      {open && (
        <div className={css.roomsList}>
          {rooms.length === 0 ? (
            <Text size="sm" variant="muted">
              No rooms returned.
            </Text>
          ) : (
            rooms.map((room, i) => <Room key={room.roomId ?? i} room={room} />)
          )}
        </div>
      )}
    </div>
  );
};

const Room = ({ room }: { room: RoomLike }) => {
  const [open, setOpen] = useState(false);
  const adults = room.pax?.adults ?? 0;
  const children = room.pax?.children?.length ?? 0;
  return (
    <div className={css.room}>
      <button
        type="button"
        className={css.roomHeader}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown /> : <ChevronRight />}
        <Text size="sm" weight="semibold">
          {room.roomName ?? 'Room'}
        </Text>
        <Badge>{room.numberOfAvailableRooms ?? 0} avail.</Badge>
        <div className={css.metaLine}>
          <PersonStanding className={css.metaIcon} />
          <Text size="xs" variant="muted">
            {adults}A {children}C
          </Text>
        </div>
      </button>
      {open && (
        <div className={css.roomBody}>
          {(room.beddingConfigurations ?? []).length > 0 && (
            <div className={css.bedList}>
              {(room.beddingConfigurations ?? []).map((b, i) => (
                <Badge key={i}>
                  <BedDouble className={css.metaIcon} /> {b.quantity ?? 1}×{' '}
                  {b.type ?? 'bed'}
                </Badge>
              ))}
            </div>
          )}
          <div className={css.ratesGrid}>
            {(room.ratePlans ?? []).map((rp, i) => (
              <div className={css.ratePlan} key={rp.ratePlanId ?? i}>
                <Text size="sm" weight="semibold">
                  {rp.ratePlanName ?? 'Rate'}
                </Text>
                <Text size="sm" variant="muted">
                  {rp.payment ?? '—'}
                </Text>
                {rp.inclusivePrice && (
                  <Text size="sm" weight="semibold">
                    {rp.inclusivePrice.amount} {rp.inclusivePrice.currency}
                  </Text>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
